import { Injectable } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
  ValidatorFn,
  AbstractControlOptions
} from '@angular/forms';
import { Fields, Validation, Field } from '@modules/dynamic-form-builder/model/formBuilderInterface';
import { fieldTypes } from '@constants/constant';
import { ADDRESS_FORM_CONTROL_NAME } from '@modules/dynamic-form-builder/constants/constant';
import { dateValidator } from '../../../validators/date.validator';
import { timeValidator } from '../../../validators/time.validator';
import { dateRangeValidator } from '../../../validators/date-range-validator';
import { CommonService } from '@shared/services/common.service';

@Injectable({
  providedIn: 'root'
})
export class DynamicFormBuilderService {
  constructor(private fb: FormBuilder, private cs: CommonService) {}

  toFormGroup(fields: Fields): FormGroup {
    const group = {};
    let passwordMismatchField: Boolean = false;
    let hasAddressField = false;
    fields?.sections.forEach((section) => {
      section.fields.forEach((field) => {
        if (field.type === fieldTypes.ADDRESS) {
          hasAddressField = true;
        }
        if(field.validation?.mismatch) passwordMismatchField = true;
        const validators = this.getValidators(field.validation);
        switch (field.type) {
          case fieldTypes.CHECKBOX:
          case fieldTypes.CUSTOM_INPUT_LIST:
            group[field.name] = [field.defaultValue || [], validators];
            break;

          case fieldTypes.SELECT:
            group[field.name] = [field.defaultValue || null, validators];
            break;
          case fieldTypes.DATE_PICKER:
            group[field.name] = [
              field.defaultValue || null,
              [dateValidator(), ...validators]
            ];
            break;
          case fieldTypes.TIME_PICKER:
            group[field.name] = [
              field.defaultValue || null,
              [timeValidator(), ...validators]
            ];
            break;
          case fieldTypes.DISCLAIMER_MESSAGE:
            break;
          case fieldTypes.MAP_IFRAME:
            group[field.name] = [field.defaultValue ?? null, validators];
            break;
          default:
            group[field.name] = [field.defaultValue || '', validators];
        }
      });
    });

    if (hasAddressField) {
      if (group[ADDRESS_FORM_CONTROL_NAME.LATITUDE] === undefined) {
        group[ADDRESS_FORM_CONTROL_NAME.LATITUDE] = [''];
      }
      if (group[ADDRESS_FORM_CONTROL_NAME.LONGITUDE] === undefined) {
        group[ADDRESS_FORM_CONTROL_NAME.LONGITUDE] = [''];
      }
    }

    if(passwordMismatchField) {
      return this.fb.group(group, { validator: this.cs.checkMatch } as AbstractControlOptions)
    }

    return this.fb.group(group);
  }

  getField(
    formFields: Fields,
    sectionIndex: number,
    fieldName: string
  ): Field | null {
    return (
      formFields.sections?.[sectionIndex]?.fields.find(
        (f: any) => f.name === fieldName
      ) || null
    );
  }

  getValidators(validatorsConfig: Validation) {
    const validators = [];
    if (validatorsConfig) {
      if (validatorsConfig.required) {
        validators.push(Validators.required);
      }
      if (validatorsConfig.maxlength) {
        validators.push(Validators.maxLength(validatorsConfig.maxlength.value));
      }
      if (validatorsConfig.minlength) {
        validators.push(Validators.minLength(validatorsConfig.minlength.value));
      }
      if (validatorsConfig.max) {
        validators.push(Validators.max(validatorsConfig.max.value));
      }
      if (validatorsConfig.min) {
        validators.push(Validators.min(validatorsConfig.min.value));
      }
      if (validatorsConfig.pattern) {
        validators.push(Validators.pattern(validatorsConfig.pattern.value));
      }
      if (validatorsConfig.dateRange) {
        validators.push(
          dateRangeValidator(
            validatorsConfig.dateRange?.minDate,
            validatorsConfig.dateRange?.maxDate
          )
        );
      }
      if (validatorsConfig.validatorFunctions && validatorsConfig.validatorFunctions.length) {
        for (const fnObj of validatorsConfig.validatorFunctions) {
          validators.push(fnObj.validatorFn)
        }
      }
    }
    return validators;
  }

  prefillForm(form, data = {}): void {
    if (form) form.patchValue(data);
  }

  addValidation(control: AbstractControl, validator: ValidatorFn): void {
    if (control && validator) {
      const currentValidators = control.validator ? [control.validator] : [];
      control.setValidators([...currentValidators, validator]);
      control.updateValueAndValidity();
    }
  }

  removeValidation(control: AbstractControl, validator: ValidatorFn): void {
    if (control && validator) {
      const currentValidators = control.validator ? [control.validator] : [];
      const updatedValidators = currentValidators.filter(
        (v) => v !== validator
      );
      control.setValidators(updatedValidators);
      control.updateValueAndValidity();
    }
  }

  addControl(form, field: Field) {
    if (field) {
      const validators = this.getValidators(field.validation);
      form.addControl(
        field.name,
        this.fb.control(field.defaultValue, validators)
      );
    }
  }
  removeControl(form: FormGroup, fieldName: string) {
    if (form.contains(fieldName)) {
      form.removeControl(fieldName);
    }
  }
}
